import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, LogOut } from "lucide-react";

const emptyListing = { manufacturer: "", model: "", year: 2024, price_usd: 0, price_on_request: false, registration: "", serial_number: "", aircraft_type: "Business Jet", region: "Global", flight_hours: 0, engine_type: "", status: "Available", description: "", image_url: "", images_text: "" };
const emptyInsight = { title: "", category: "Market Report", excerpt: "", body: "", author: "JS Aviation Research", cover_image: "", published: true };

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [insights, setInsights] = useState([]);
  const [lForm, setLForm] = useState(emptyListing);
  const [iForm, setIForm] = useState(emptyInsight);

  useEffect(() => {
    if (!loading && (user === false || !user)) nav("/login");
  }, [user, loading, nav]);

  const loadAll = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/listings"),
        api.get("/admin/insights"),
      ]);
      setListings(a.data); setInsights(b.data);
    } catch {}
  };
  useEffect(() => { if (user && user !== false) loadAll(); }, [user]);

  const createListing = async (e) => {
    e.preventDefault();
    try {
      const images = (lForm.images_text || "").split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const { images_text, ...rest } = lForm;
      await api.post("/admin/listings", {
        ...rest,
        images,
        year: Number(lForm.year),
        price_usd: Number(lForm.price_usd),
        flight_hours: Number(lForm.flight_hours),
      });
      toast.success("Listing created");
      setLForm(emptyListing); loadAll();
    } catch (e) { toast.error("Failed"); }
  };
  const deleteListing = async (id) => { await api.delete(`/admin/listings/${id}`); loadAll(); };

  const createInsight = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/insights", iForm);
      toast.success("Insight published");
      setIForm(emptyInsight); loadAll();
    } catch { toast.error("Failed"); }
  };
  const deleteInsight = async (id) => { await api.delete(`/admin/insights/${id}`); loadAll(); };

  if (loading || !user || user === false) return null;

  return (
    <main data-testid="page-admin" className="pt-32 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">Admin dashboard</div>
            <h1 className="font-serif text-[36px] md:text-[48px] text-[#0E1B2E] tracking-tight mt-2">Content management</h1>
          </div>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="btn-ghost px-4 py-2 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2"><LogOut size={14} /> Log out</button>
        </div>

        <div className="flex gap-2 mb-8 border-b border-[#0E1B2E]/10 overflow-x-auto">
          {["listings", "insights"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`shrink-0 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.28em] border-b-2 -mb-px ${tab === t ? "text-[#A5854C] border-[#C8A97E]" : "text-[#9AA3AE] border-transparent hover:text-[#0E1B2E]"}`}>{t}</button>
          ))}
        </div>

        {tab === "listings" ? (
          <div className="grid grid-cols-12 gap-8">
            <form onSubmit={createListing} className="col-span-12 lg:col-span-5 border border-[#0E1B2E]/10 bg-white p-6 space-y-4" data-testid="listing-form">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#A5854C]">New listing</div>
              {[["manufacturer","Manufacturer"],["model","Model"],["registration","Registration"],["serial_number","Serial No."],["year","Year"],["price_usd","Price USD"],["flight_hours","Flight Hours"],["engine_type","Engine Type"],["region","Region"]].map(([k,label]) => (
                <label key={k} className="block">
                  <span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">{label}</span>
                  <input required={["manufacturer","model","year"].includes(k)} value={lForm[k]} onChange={e => setLForm({ ...lForm, [k]: e.target.value })} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-2 text-[#0E1B2E] text-[14px] outline-none" />
                </label>
              ))}
              <label className="flex items-center gap-3 py-1">
                <input type="checkbox" checked={lForm.price_on_request} onChange={e => setLForm({ ...lForm, price_on_request: e.target.checked })} className="accent-[#A5854C] w-4 h-4" />
                <span className="text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono">Show "Price on request" instead of price</span>
              </label>
              <label className="block"><span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">Image URLs (one per line, or comma-separated)</span>
                <textarea value={lForm.images_text} onChange={e => setLForm({ ...lForm, images_text: e.target.value })} rows={4} placeholder="https://…/photo1.jpg&#10;https://…/photo2.jpg" className="w-full bg-transparent border border-[#0E1B2E]/15 focus:border-[#A5854C] p-2 text-[#0E1B2E] text-[13px] outline-none" />
              </label>
              <label className="block"><span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">Type</span>
                <select value={lForm.aircraft_type} onChange={e => setLForm({ ...lForm, aircraft_type: e.target.value })} className="w-full bg-white border border-[#0E1B2E]/10 py-2 text-[#0E1B2E] text-[14px]">
                  {["Business Jet","Turboprop","Piston","Trainer","Helicopter"].map(o => <option key={o}>{o}</option>)}
                </select>
              </label>
              <label className="block"><span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">Status</span>
                <select value={lForm.status} onChange={e => setLForm({ ...lForm, status: e.target.value })} className="w-full bg-white border border-[#0E1B2E]/10 py-2 text-[#0E1B2E] text-[14px]">
                  {["Available","Under Offer","Sold"].map(o => <option key={o}>{o}</option>)}
                </select>
              </label>
              <label className="block"><span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">Description</span>
                <textarea value={lForm.description} onChange={e => setLForm({ ...lForm, description: e.target.value })} rows={3} className="w-full bg-transparent border border-[#0E1B2E]/15 focus:border-[#A5854C] p-2 text-[#0E1B2E] text-[14px] outline-none" />
              </label>
              <button data-testid="listing-create" className="btn-gold w-full py-3 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center justify-center gap-2"><Plus size={14} /> Create Listing</button>
            </form>

            <div className="col-span-12 lg:col-span-7 space-y-3">
              {listings.length === 0 && <div className="text-[#9AA3AE] text-[13px]">No listings yet.</div>}
              {listings.map(l => (
                <div key={l.id} className="border border-[#0E1B2E]/10 bg-white p-5 flex justify-between items-start">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A5854C]">{l.manufacturer} · {l.status}</div>
                    <div className="font-serif text-[20px] text-[#0E1B2E] mt-1">{l.model} · {l.year}</div>
                    <div className="text-[12px] text-[#6B7684] mt-1">{l.region} · {l.flight_hours || 0} hrs · {l.engine_type || "—"}</div>
                  </div>
                  <button onClick={() => deleteListing(l.id)} className="text-[#9AA3AE] hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-8">
            <form onSubmit={createInsight} className="col-span-12 lg:col-span-5 border border-[#0E1B2E]/10 bg-white p-6 space-y-4" data-testid="insight-form">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#A5854C]">New insight</div>
              {[["title","Title"],["category","Category"],["author","Author"],["cover_image","Cover Image URL"]].map(([k,label]) => (
                <label key={k} className="block">
                  <span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">{label}</span>
                  <input required={k==="title"} value={iForm[k]} onChange={e => setIForm({ ...iForm, [k]: e.target.value })} className="w-full bg-transparent border-b border-[#0E1B2E]/15 focus:border-[#A5854C] py-2 text-[#0E1B2E] text-[14px] outline-none" />
                </label>
              ))}
              <label className="block"><span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">Excerpt</span>
                <textarea required value={iForm.excerpt} onChange={e => setIForm({ ...iForm, excerpt: e.target.value })} rows={2} className="w-full bg-transparent border border-[#0E1B2E]/15 focus:border-[#A5854C] p-2 text-[#0E1B2E] text-[14px] outline-none" />
              </label>
              <label className="block"><span className="block text-[11px] uppercase tracking-[0.22em] text-[#9AA3AE] font-mono mb-1">Body</span>
                <textarea required value={iForm.body} onChange={e => setIForm({ ...iForm, body: e.target.value })} rows={6} className="w-full bg-transparent border border-[#0E1B2E]/15 focus:border-[#A5854C] p-2 text-[#0E1B2E] text-[14px] outline-none" />
              </label>
              <button data-testid="insight-create" className="btn-gold w-full py-3 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center justify-center gap-2"><Plus size={14} /> Publish Insight</button>
            </form>
            <div className="col-span-12 lg:col-span-7 space-y-3">
              {insights.length === 0 && <div className="text-[#9AA3AE] text-[13px]">No insights yet.</div>}
              {insights.map(a => (
                <div key={a.id} className="border border-[#0E1B2E]/10 bg-white p-5 flex justify-between items-start">
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A5854C]">{a.category} · {a.author}</div>
                    <div className="font-serif text-[20px] text-[#0E1B2E] mt-1">{a.title}</div>
                    <div className="text-[13px] text-[#6B7684] mt-2 max-w-xl">{a.excerpt}</div>
                  </div>
                  <button onClick={() => deleteInsight(a.id)} className="text-[#9AA3AE] hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
